import { useEffect, useState, useContext } from "react";
import {
  Button,
  TextField,
  Paper,
  Typography,
  Box
} from "@mui/material";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import Swal from "sweetalert2";

const Login = () => {
  const { user } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const navigate = useNavigate();

  const Toast = Swal.mixin({
    toast: true,
    position: "top-right",
    showConfirmButton: false,
    timer: 2500,
    timerProgressBar: true,
  });

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const validateEmail = (value) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailError(regex.test(value) ? "" : "Invalid email format");
  };

  const validatePassword = (value) => {
    setPasswordError(value.length >= 6 ? "" : "Password must be 6+ characters");
  };

  const handleLogin = async () => {
    if (!email || !password) {
      return Toast.fire({
        icon: "warning",
        title: "Please fill all fields",
      });
    }

    if (emailError || passwordError) {
      return Toast.fire({
        icon: "warning",
        title: "Please fix validation errors",
      });
    }

    try {
      const userCred = await signInWithEmailAndPassword(auth, email, password);
      const snap = await getDoc(doc(db, "users", userCred.user.uid));

      if (!snap.exists()) {
        await auth.signOut(); // ✅ Safety logout
        return Toast.fire({
          icon: "error",
          title: "User record not found!",
        });
      }

      const role = snap.data().role;

      if (role === "Superadmin") {
        Toast.fire({
          icon: "success",
          title: "Login Successful!",
        });
        setTimeout(() => navigate("/dashboard"), 800);
      } else {
        await auth.signOut(); // ✅ Prevent unauthorized login
        Toast.fire({
          icon: "error",
          title: "Access Denied! Only Superadmin can login.",
        });
        setTimeout(() => navigate("/login"), 800);
      }

    } catch (err) {
      const msg = err.message.includes("auth/user-not-found")
        ? "Invalid Email!"
        : "Incorrect Email or Password";

      Toast.fire({
        icon: "error",
        title: msg,
      });
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(135deg, #1e293b, #0f172a)"
      }}
    >
      <Paper
        elevation={10}
        sx={{
          padding: 4,
          width: 350,
          textAlign: "center",
          borderRadius: "15px",
        }}
      >
        <img src="/logo.jpeg" alt="Logo" style={{ width: "120px" }} />

        <Typography variant="h5" sx={{ mt: 1, fontWeight: "bold", color: "#0f172a" }}>
         MMIS Login
        </Typography>

        <TextField
          fullWidth
          size="small"
          label="Email"
          variant="outlined"
          sx={{ mt: 3 }}
          error={Boolean(emailError)}
          helperText={emailError}
          onChange={(e) => {
            setEmail(e.target.value);
            validateEmail(e.target.value);
          }}
        />

        <TextField
          fullWidth
          size="small"
          label="Password"
          type="password"
          variant="outlined"
          sx={{ mt: 2 }}
          error={Boolean(passwordError)}
          helperText={passwordError}
          onChange={(e) => {
            setPassword(e.target.value);
            validatePassword(e.target.value);
          }}
        />

        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={!email || !password || emailError || passwordError}
          sx={{
            mt: 3,
            backgroundColor: "#146C94",
            "&:hover": { backgroundColor: "#0e4e68" },
          }}
          onClick={handleLogin}
        >
          Login
        </Button>

        <Typography sx={{ mt: 2, fontSize: "14px", color: "#555" }}>
          © Powered by Bluevipers Technology.
        </Typography>
      </Paper>
    </Box>
  );
};

export default Login;
